/**
 * Display helpers for timezone rows and the "your local time" preview.
 *
 * Everything here degrades to null instead of throwing: the server's zone
 * list can contain names this browser's ICU does not know, and such a row
 * must still render (name + server offset only), so every Intl call is
 * wrapped. Offsets always come from the server's `offset_minutes` — the
 * server computed them DST-aware at request time.
 */

/** 330 -> "UTC+5:30", -240 -> "UTC-4:00"; null for a non-finite input (the
 * synthetic option used when the stored zone is missing from the list). */
export const formatUtcOffset = (offsetMinutes: number): string | null => {
  if (!Number.isFinite(offsetMinutes)) return null;
  const sign = offsetMinutes < 0 ? '-' : '+';
  const abs = Math.abs(offsetMinutes);
  const hours = Math.floor(abs / 60);
  const minutes = abs % 60;
  return `UTC${sign}${hours}:${String(minutes).padStart(2, '0')}`;
};

/** "India Standard Time" for Asia/Kolkata; null where this browser's Intl
 * does not know the zone (the row then shows name + server offset only). */
export const zoneLongName = (zoneName: string): string | null => {
  try {
    const parts = new Intl.DateTimeFormat('en', {
      timeZone: zoneName,
      timeZoneName: 'long',
    }).formatToParts(new Date());
    const part = parts.find((p) => p.type === 'timeZoneName');
    return part ? part.value : null;
  } catch {
    return null;
  }
};

/** The current wall clock in the zone, "HH:MM" (h23); null when unknown. */
export const zoneCurrentTime = (zoneName: string): string | null => {
  try {
    return new Intl.DateTimeFormat('en', {
      timeZone: zoneName,
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).format(new Date());
  } catch {
    return null;
  }
};
